/**
 * タイムアウト・リトライ機能付きユーティリティ関数
 * 
 * Exponential Backoff戦略を採用し、一時的なネットワークエラーに対応
 */

export interface RetryOptions {
  maxRetries?: number;
  timeoutMs?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
}

export interface FetchError extends Error {
  code: string;
  details?: any;
  timestamp: string;
  isRetryable: boolean;
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  timeoutMs: 10000, // 10秒
  initialDelayMs: 1000, // 1秒
  maxDelayMs: 8000 // 8秒
};

/**
 * 待機関数
 */
const wait = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Exponential Backoff計算
 */
const calculateDelay = (attempt: number, initialDelay: number, maxDelay: number): number => {
  const exponentialDelay = initialDelay * Math.pow(2, attempt);
  return Math.min(exponentialDelay, maxDelay);
};

/**
 * タイムアウト付きPromise実行
 */
async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorMessage: string = 'Operation timed out'
): Promise<T> {
  let timeoutId: NodeJS.Timeout;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      const error = new Error(errorMessage) as FetchError;
      error.code = 'TIMEOUT';
      error.timestamp = new Date().toISOString();
      error.isRetryable = true;
      reject(error);
    }, timeoutMs);
  });

  try {
    const result = await Promise.race([promise, timeoutPromise]);
    clearTimeout(timeoutId!);
    return result;
  } catch (error) {
    clearTimeout(timeoutId!);
    throw error;
  }
}

/**
 * エラーがリトライ可能かどうかを判定
 */
export function isRetryableError(error: any): boolean {
  if (!error) return false;

  // AWS Amplify のエラー名を確認
  const errorName = error.name || '';
  const errorMessage = error.message || '';

  // リトライ不可のエラー（認証系エラー）
  const nonRetryableErrors = [
    'NotAuthorizedException',
    'UserNotFoundException',
    'InvalidParameterException',
    'ResourceNotFoundException'
  ];

  if (nonRetryableErrors.includes(errorName)) {
    return false;
  }

  // リトライ可能なエラー（ネットワーク系、タイムアウト系）
  const retryableErrors = [
    'NetworkError',
    'TimeoutError',
    'ServiceUnavailable',
    'TooManyRequestsException',
    'InternalErrorException'
  ];

  if (retryableErrors.includes(errorName)) {
    return true;
  }

  // メッセージベースの判定
  const retryableMessages = [
    'timeout',
    'network',
    'connection',
    'ECONNREFUSED',
    'ENOTFOUND',
    'ETIMEDOUT'
  ];

  return retryableMessages.some(msg => 
    errorMessage.toLowerCase().includes(msg.toLowerCase())
  );
}

/**
 * エラー情報を構造化
 */
export function createStructuredError(
  error: any,
  attempt: number,
  isRetryable: boolean
): FetchError {
  const structuredError: FetchError = {
    name: error.name || 'UnknownError',
    message: error.message || 'An unknown error occurred',
    code: error.code || error.name || 'UNKNOWN_ERROR',
    details: {
      attempt,
      originalError: error,
      stack: error.stack
    },
    timestamp: new Date().toISOString(),
    isRetryable
  };

  return structuredError;
}

/**
 * リトライ付きで非同期関数を実行
 */
export async function fetchWithRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let lastError: FetchError | null = null;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      // タイムアウト付きで実行
      const result = await withTimeout(
        fn(),
        opts.timeoutMs,
        `Operation timed out after ${opts.timeoutMs}ms (attempt ${attempt + 1}/${opts.maxRetries + 1})`
      );

      // 成功したらログを出力
      if (attempt > 0) {
        console.log(`✅ Operation succeeded on attempt ${attempt + 1}`);
      }

      return result;

    } catch (error: any) {
      const isRetryable = isRetryableError(error);
      const structuredError = createStructuredError(error, attempt + 1, isRetryable);
      lastError = structuredError;

      console.error(`❌ Attempt ${attempt + 1}/${opts.maxRetries + 1} failed:`, {
        code: structuredError.code,
        message: structuredError.message,
        isRetryable: structuredError.isRetryable,
        timestamp: structuredError.timestamp
      });

      // 最後の試行、またはリトライ不可のエラーの場合
      if (attempt === opts.maxRetries || !isRetryable) {
        throw structuredError;
      }

      // リトライ前の待機
      const delay = calculateDelay(attempt, opts.initialDelayMs, opts.maxDelayMs);
      console.log(`⏳ Retrying in ${delay}ms...`);
      await wait(delay);
    }
  }

  // ここには到達しないはずだが、念のため
  throw lastError || new Error('Unknown error occurred');
}

